import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CasesBackendService, InventoryApiService, P2pApiNewService, ReferralsApiService } from '@dev-fast/backend-services';
import {
  type FilterOptions,
  type ICaseItemDtoV2,
  type IInventoryItemDetailed,
  type IKitResponseDto,
  type IMarketplaceKitData,
  type IReferralBonus,
  type SocketResponse,
  ECaseType,
  MAIN_LANG,
  MarketSortingTypes,
  ModalNames,
  NotificationStatus,
  NotificationType,
} from '@dev-fast/types';
import { RouterNavigation } from '@ngxs/router-plugin';
import { type StateContext, Action, Selector, State, Store } from '@ngxs/store';
import { filter as filterLodash } from 'lodash-es';
import { type Observable, catchError, EMPTY, tap } from 'rxjs';
import { finalize } from 'rxjs';

import { LanguageService, LanguageState, SetLanguage } from '@app/core/language-service';
import { NotificationsService } from '@app/core/notification-service';
import { ModalsState, OpenModal } from '@app/core/state/modals';
import type { RouterStateParams } from '@app/core/state/utils';

import {
  GetAvailabilityItemInCases,
  GetCaseItemDetailsWithTranslate,
  GetFullSkinData,
  GetItemAvailabilityOnThenMarket,
  SetItemDetailsLoadingStatus,
} from './skins.actions';
import { type SkinsStateModel, SKINS_INITIAL_STATE } from './skins-state.model';

@State<SkinsStateModel>({
  defaults: SKINS_INITIAL_STATE,
  name: 'skins',
})
@Injectable()
export class SkinsState {
  readonly #p2pService = inject(P2pApiNewService);
  readonly #apiService = inject(ReferralsApiService);
  readonly #casesBackendService = inject(CasesBackendService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #inventoryBackendService = inject(InventoryApiService);

  readonly #router = inject(Router);
  readonly #store = inject(Store);

  constructor() {
    this.#apiService
      .bonusCaseReceived()
      .pipe(tap((value) => this.#handleBonusCase(value)))
      .subscribe();
  }

  @Selector()
  static itemAvailabilityInCases({ itemAvailabilityInCases }: SkinsStateModel): ICaseItemDtoV2[] | null {
    return itemAvailabilityInCases;
  }

  @Selector()
  static itemDetailsRequestState({ itemDetailsRequestState }: SkinsStateModel): boolean | null {
    return itemDetailsRequestState;
  }

  @Selector()
  static itemDetails({ itemDetails }: SkinsStateModel): IInventoryItemDetailed | null {
    return itemDetails;
  }

  @Selector()
  static direction({ direction }: SkinsStateModel): string {
    return direction;
  }

  @Selector()
  static itemAvailabilityOnThenMarket({ itemAvailabilityOnThenMarket }: SkinsStateModel): IMarketplaceKitData[] | null {
    return itemAvailabilityOnThenMarket;
  }

  @Selector()
  static isLoadingItemDetails({ isLoadingItemDetails }: SkinsStateModel): boolean {
    return isLoadingItemDetails;
  }

  @Action(GetFullSkinData)
  getFullData({ dispatch, patchState }: StateContext<SkinsStateModel>, { params }: GetFullSkinData): void {
    const { shortName, skin, name } = params;
    const lang = this.#getLang();
    const decodedName = name;
    const decodedShortName = this.#decodeName(shortName);
    const decodedSkin = this.#decodeName(skin);

    patchState({
      itemDetailedMeta: {
        shortName: decodedShortName,
        skin: decodedSkin,
        name: decodedName,
        lang,
      },
      itemAvailabilityInCases: null,
    });

    dispatch(new GetCaseItemDetailsWithTranslate({ name: decodedName, lang, withSimilar: true }));
  }

  @Action(SetItemDetailsLoadingStatus)
  SetItemDetailsLoadingStatus({ patchState }: StateContext<SkinsStateModel>, { isLoadingItemDetails }: SetItemDetailsLoadingStatus): void {
    patchState({ isLoadingItemDetails });
  }

  @Action(RouterNavigation<RouterStateParams>)
  navigate({ dispatch }: StateContext<SkinsStateModel>, { routerState }: RouterNavigation<RouterStateParams>): void {
    const { params, url } = routerState;
    const isSkinItemPage = url.includes('/items/item/');
    const isModalOpened = url.includes('modal:transaction');
    if (isSkinItemPage && !isModalOpened && params) {
      const name = params['item'];

      if (name) {
        dispatch(new GetFullSkinData({ name }));
      }
    }
  }

  @Action(GetAvailabilityItemInCases)
  getAvailabilityItemInCases(
    { patchState }: StateContext<SkinsStateModel>,
    { params }: GetAvailabilityItemInCases,
  ): Observable<ICaseItemDtoV2[]> {
    return this.#casesBackendService.getAvailabilityItemInCases(params).pipe(
      tap((response) => {
        // Убираем кейсы за билеты и с type: free
        const filteredCases = filterLodash(response, (caseItem) => !caseItem.availableForBonus && caseItem.type !== ECaseType.FREE);

        patchState({
          itemAvailabilityInCases: filteredCases,
        });
      }),
      catchError((error) => {
        patchState({
          itemAvailabilityInCases: [],
        });
        return this.#onError(error);
      }),
    );
  }

  @Action(GetCaseItemDetailsWithTranslate, { cancelUncompleted: true })
  getCaseItemDetailWithTranslate(
    { patchState, dispatch }: StateContext<SkinsStateModel>,
    { params }: GetCaseItemDetailsWithTranslate,
  ): Observable<IInventoryItemDetailed[] | []> {
    dispatch(new SetItemDetailsLoadingStatus(true));
    return this.#inventoryBackendService.getInventoryItemDetailedWithTranslate(params).pipe(
      tap((response) => {
        const [detailedCaseItem] = response;
        const mergeSimilar = response.map((item) => item.similar).flat(1);
        if (detailedCaseItem) {
          detailedCaseItem.similar = mergeSimilar;
        }
        patchState({
          itemDetails: detailedCaseItem,
          itemDetailsRequestState: true,
        });
        if (!detailedCaseItem) {
          return;
        }
        const req: Partial<FilterOptions> = {
          sortBy: MarketSortingTypes.MIN_PRICE,
          query: detailedCaseItem.baseItemName.replace(/ *\([^)]*\) */g, '').replace('StatTrak™ ', ''),
        };
        dispatch(new GetItemAvailabilityOnThenMarket(req));

        const availabilityRequestParams = this.#getItemAvailabilityParams(detailedCaseItem);

        if (availabilityRequestParams) {
          dispatch(new GetAvailabilityItemInCases(availabilityRequestParams));
        }
      }),
      catchError((error) => {
        if (error.status === 404) {
          this.#router.navigate(['not-found'], { replaceUrl: true });
        }
        patchState({
          itemDetails: null,
          itemDetailsRequestState: false,
        });
        return this.#onError(error);
      }),
      finalize(() => {
        dispatch(new SetItemDetailsLoadingStatus(false));
      }),
    );
  }

  @Action(GetItemAvailabilityOnThenMarket, { cancelUncompleted: true })
  getItems({ patchState }: StateContext<SkinsStateModel>, { params }: GetItemAvailabilityOnThenMarket): Observable<IKitResponseDto> {
    return this.#p2pService.getMarketItemsV2(params).pipe(
      tap((response) => {
        patchState({
          itemAvailabilityOnThenMarket: response.kits,
        });
      }),
      catchError((error) => this.#onError(error)),
    );
  }

  @Action(SetLanguage, { cancelUncompleted: true })
  changeLocale({ dispatch, getState, patchState }: StateContext<SkinsStateModel>, { locale }: SetLanguage): void {
    const { itemDetailedMeta } = getState();

    if (itemDetailedMeta) {
      let lang = locale.path.substring(0, 2).toUpperCase();
      if (!LanguageService.isAcceptedLanguageCode(locale.path)) {
        lang = MAIN_LANG.toUpperCase();
      }
      patchState({ itemDetailedMeta: { ...itemDetailedMeta, lang } });
      dispatch(new GetCaseItemDetailsWithTranslate({ name: itemDetailedMeta.name, lang, withSimilar: true }));
    }
  }

  #decodeName(name = ''): string {
    return decodeURIComponent(name.replace(/_/g, ' '));
  }

  #getItemAvailabilityParams(itemDetails: IInventoryItemDetailed): { shortName: string; skin: string } | null {
    const exactItem = itemDetails.similar.find((item) => item.name === itemDetails.name && item.shortName && item.skin);
    const itemWithBaseInfo = exactItem || itemDetails.similar.find((item) => item.shortName && item.skin);

    if (!itemWithBaseInfo?.shortName || !itemWithBaseInfo.skin) {
      return null;
    }

    return {
      shortName: itemWithBaseInfo.shortName,
      skin: itemWithBaseInfo.skin,
    };
  }

  #getLang(): string {
    const snapshotLang = this.#store.selectSnapshot(LanguageState.lang).path;
    const isAcceptedLanguageCode = LanguageService.isAcceptedLanguageCode(snapshotLang);

    return isAcceptedLanguageCode ? snapshotLang.substring(0, 2).toUpperCase() : MAIN_LANG.toUpperCase();
  }

  #handleBonusCase(value: SocketResponse<IReferralBonus>): void {
    if (!this.#store.selectSnapshot(ModalsState.activeModals).includes(ModalNames.CASES_BONUS)) {
      this.#store.dispatch(new OpenModal(ModalNames.CASES_BONUS, value.payload));
    }
  }

  #onError(errorResponse: HttpErrorResponse): Observable<never> {
    this.#notificationsService.addNotification({
      id: Date.now(),
      type: errorResponse.error?.type ? errorResponse.error.type : NotificationType.Error,
      icon: 'warning',
      message: this.#errorMessageFormat(errorResponse),
      createDate: Date.now(),
      system: true,
      status: NotificationStatus.new,
    });
    return EMPTY;
  }

  #errorMessageFormat(errorResponse: HttpErrorResponse): string {
    if (errorResponse.message) {
      return errorResponse.message;
    }
    if (typeof errorResponse.error === 'string') {
      return errorResponse.error;
    }
    return 'Error';
  }
}
